import { FunctionComponent, useContext, useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { Navigate, useNavigate } from "react-router-dom";
import ToasterWithMessage from "../../components/shared-components/toaster";
import { roles } from "../../helper";
import { getRolePageAccess, getRolePageCatalog, updateRolePageAccess } from "../../services/rolePageAccessApi";
import { getRoleAllowedPageKeys, getRoleDefaultPageKeys } from "../../constants/pageAccess";
import { globalContext } from "../../Provider/AppProvider";
import styles from "./RolePageAccess.module.css";

type CatalogItem = {
  pageKey: string;
  label: string;
  routePath: string;
};

/** Affiliates do not use this admin dashboard — only staff roles are configurable here. */
const ROLE_OPTIONS = [
  { value: roles.MARKETING_USER, label: "Sales Rep User" },
  { value: roles.SUB_ADMIN, label: "Admin" },
];

const RolePageAccess: FunctionComponent = () => {
  const navigate = useNavigate();
  const { success, error } = ToasterWithMessage();
  const { globalState } = useContext<any>(globalContext);
  const [selectedRole, setSelectedRole] = useState<string>(roles.MARKETING_USER);
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [selectedPageKeys, setSelectedPageKeys] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const scopedCatalog = useMemo(() => {
    const allowedKeys = getRoleAllowedPageKeys(selectedRole);
    if (!allowedKeys.length) {
      return [];
    }
    return catalog.filter((item) => allowedKeys.includes(item.pageKey));
  }, [catalog, selectedRole]);

  const allSelected = useMemo(() => {
    if (!scopedCatalog.length) {
      return false;
    }
    return scopedCatalog.every((item) => selectedPageKeys.includes(item.pageKey));
  }, [scopedCatalog, selectedPageKeys]);

  const loadAccess = async (roleKey: string) => {
    try {
      setIsLoading(true);
      const [catalogResponse, roleAccessResponse] = await Promise.all([
        getRolePageCatalog(),
        getRolePageAccess(roleKey),
      ]);

      const catalogItems = (catalogResponse?.pages || []) as CatalogItem[];
      const rolePageKeys = (roleAccessResponse?.pageKeys || []) as string[];
      const allowedRoleKeys = getRoleAllowedPageKeys(roleKey);
      const defaultRoleKeys = getRoleDefaultPageKeys(roleKey);
      const effectivePageKeys = rolePageKeys.length ? rolePageKeys : defaultRoleKeys;

      setCatalog(catalogItems);
      setSelectedPageKeys(effectivePageKeys.filter((pageKey) => allowedRoleKeys.includes(pageKey)));
    } catch (err: any) {
      error(err?.message || "Failed to load role page access.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadAccess(selectedRole);
  }, [selectedRole]);

  const handleTogglePage = (pageKey: string) => {
    setSelectedPageKeys((previous) =>
      previous.includes(pageKey) ? previous.filter((key) => key !== pageKey) : [...previous, pageKey],
    );
  };

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedPageKeys([]);
      return;
    }
    setSelectedPageKeys(scopedCatalog.map((item) => item.pageKey));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const allowedRoleKeys = getRoleAllowedPageKeys(selectedRole);
      const filteredPageKeys = selectedPageKeys.filter((pageKey) => allowedRoleKeys.includes(pageKey));
      await updateRolePageAccess(selectedRole, filteredPageKeys);
      success("Role page access updated successfully.");
    } catch (err: any) {
      error(err?.message || "Failed to save role page access.");
    } finally {
      setIsSaving(false);
    }
  };

  if (globalState?.profile?.userRole !== roles.ADMIN) {
    return <Navigate to="/no-access" replace />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2 className={styles.title}>Role Page Access</h2>
        <p className={styles.subtitle}>Select which pages are visible and accessible for each role.</p>
        <div className={styles.topTabs}>
          <button className={`${styles.topTabButton} ${styles.topTabButtonActive}`}>Role Access</button>
          <button className={styles.topTabButton} onClick={() => navigate("/admin-access")}>
            Admin Access
          </button>
        </div>
      </div>

      <div className={styles.roleRow}>
        <label htmlFor="role-select" className={styles.roleLabel}>
          Role
        </label>
        <select
          id="role-select"
          value={selectedRole}
          onChange={(event) => setSelectedRole(event.target.value)}
          className={styles.roleSelect}
        >
          {ROLE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className={styles.loaderWrap}>
          <CircularProgress size={30} />
        </div>
      ) : (
        <div className={styles.card}>
          <div className={styles.selectAllRow}>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" checked={allSelected} onChange={handleToggleAll} />
              Select All Pages
            </label>
          </div>
          <div className={styles.pageList}>
            {scopedCatalog.map((item) => (
              <label key={item.pageKey} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={selectedPageKeys.includes(item.pageKey)}
                  onChange={() => handleTogglePage(item.pageKey)}
                />
                <span className={styles.pageLabel}>{item.label}</span>
                <span className={styles.routePath}>{item.routePath}</span>
              </label>
            ))}
            {scopedCatalog.length === 0 && (
              <p className={styles.emptyState}>No page options available for this role.</p>
            )}
          </div>
        </div>
      )}

      <div className={styles.actions}>
        <button
          onClick={handleSave}
          disabled={isLoading || isSaving}
          className={styles.saveButton}
        >
          {isSaving ? "Saving..." : "Save Access"}
        </button>
      </div>
    </div>
  );
};

export default RolePageAccess;
